import { repositoryFactory } from '@data';
import { tagMasteryCalculator, adaptiveSessionStateManager } from './index.js';

/**
 * Remediation Engine Service
 * Monitors learning struggles and triggers remediation content
 * Implements HamTestOnline model: trigger remediation at 3+ consecutive errors per tag
 * 
 * Integrates with adaptive session state to:
 * - Enforce cooldowns to prevent over-triggering
 * - Inject remediation sections out-of-order
 * - Preserve linear progression for resumption after remediation
 */
export class RemediationEngine {
  constructor() {
    this.progressRepository = repositoryFactory.getProgressRepository();
    this.courseRepository = repositoryFactory.getCourseRepository();
  }

  /**
   * Enhanced trigger detection integrating with session state
   * Checks:
   * 1. Consecutive incorrect answers (3+ for the tag)
   * 2. Low tag mastery (< 70%)
   * 3. Not on cooldown
   * 
   * Returns comprehensive trigger info including remediation section and reason
   */
  async checkRemediationTrigger(userId, courseId, tagId, sessionId = null) {
    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) {
      return null;
    }

    // Check consecutive misses from session state if available
    let consecutiveIncorrect = 0;
    
    if (sessionId) {
      try {
        const session = await repositoryFactory.getSessionRepository().getSessionById(sessionId);
        if (session) {
          let missesByTag = {};
          try {
            missesByTag = typeof session.consecutiveMissesByTag === 'string'
              ? JSON.parse(session.consecutiveMissesByTag)
              : session.consecutiveMissesByTag;
          } catch (e) {
            missesByTag = {};
          }
          consecutiveIncorrect = missesByTag[tagId] || 0;
        }
      } catch (err) {
        console.warn('[RemediationEngine] Error getting consecutive misses from session:', err);
      }
    }

    // Fallback to progress history if not in session
    if (consecutiveIncorrect === 0) {
      const course = await this.courseRepository.getCourseById(courseId);
      const recentResponses = [];

      for (const section of course.sections) {
        const questions = await this.courseRepository.getQuestionsForSection(courseId, section.id);
        
        for (const question of questions) {
          if (question.tags && question.tags.includes(tagId)) {
            const history = progress.getQuestionProgress(question.id)?.history || [];
            recentResponses.push(...history.map(h => ({
              questionId: question.id,
              tagId,
              isCorrect: h.correct ?? h.isCorrect ?? false,
              timestamp: h.timestamp,
            })));
          }
        }
      }

      // Sort by timestamp (most recent first)
      recentResponses.sort((a, b) => b.timestamp - a.timestamp);
      const recent = recentResponses.slice(0, 10);

      // Count consecutive incorrect
      for (const response of recent) {
        if (!response.isCorrect) {
          consecutiveIncorrect++;
        } else {
          break; // Stop at first correct
        }
      }
    }

    // Get tag mastery for second trigger criterion
    const mastery = await tagMasteryCalculator.calculateTagMastery(userId, courseId, tagId);
    const shouldTrigger = consecutiveIncorrect >= 3 && mastery.finalMastery < 0.7;

    console.log('[RemediationEngine.checkRemediationTrigger]', {
      tagId,
      consecutiveIncorrect,
      mastery: (mastery.finalMastery * 100).toFixed(1) + '%',
      shouldTrigger,
    });

    return {
      shouldTrigger,
      consecutiveIncorrect,
      tagId,
      masteryPercent: mastery.finalMastery,
      reason: consecutiveIncorrect >= 3 ? 'consecutive_errors' : 'low_mastery',
    };
  }

  /**
   * Find the section(s) most relevant to a tag
   * Uses tag relationships to identify content sections
   */
  async findSectionForTag(courseId, tagId) {
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) return null;

    // Count tag occurrences per section
    const sectionTagCounts = {};
    
    for (const section of course.sections) {
      const questions = await this.courseRepository.getQuestionsForSection(courseId, section.id);
      let tagCount = 0;
      
      for (const question of questions) {
        if (question.tags && question.tags.includes(tagId)) {
          tagCount++;
        }
      }
      
      if (tagCount > 0) {
        sectionTagCounts[section.id] = {
          section,
          tagCount,
        };
      }
    }

    // Return section with most questions for this tag (best relevance)
    if (Object.keys(sectionTagCounts).length === 0) {
      return null;
    }

    const bestSection = Object.entries(sectionTagCounts).reduce((best, [, current]) => {
      return current.tagCount > best.tagCount ? current : best;
    });

    return bestSection.section;
  }

  /**
   * Trigger remediation and inject section
   * Handles:
   * 1. Cooldown checking
   * 2. Section identification
   * 3. Session state injection
   * 4. Cooldown recording
   */
  async triggerRemediationWithInjection(userId, courseId, currentSectionId, tagId, sessionId = null) {
    // Check trigger
    const triggerInfo = await this.checkRemediationTrigger(userId, courseId, tagId, sessionId);
    if (!triggerInfo?.shouldTrigger) {
      return {
        triggered: false,
        reason: 'trigger_not_met',
      };
    }

    // Check cooldown if we have session
    if (sessionId) {
      const session = await repositoryFactory.getSessionRepository().getSessionById(sessionId);
      if (session && adaptiveSessionStateManager.isRemediationOnCooldown(session, tagId)) {
        console.log('[RemediationEngine] Remediation on cooldown for tag:', tagId);
        return {
          triggered: false,
          reason: 'on_cooldown',
          tagId,
        };
      }
    }

    // Find remediation section
    const remediationSection = await this.findSectionForTag(courseId, tagId);
    if (!remediationSection) {
      console.warn('[RemediationEngine] No section found for tag:', tagId);
      return {
        triggered: false,
        reason: 'no_section_found',
        tagId,
      };
    }

    // Inject section if we have session
    if (sessionId) {
      try {
        await adaptiveSessionStateManager.injectSection(
          sessionId,
          remediationSection.id,
          currentSectionId
        );

        // Set cooldown
        await adaptiveSessionStateManager.setRemediationCooldown(sessionId, tagId);

        console.log('[RemediationEngine] Remediation triggered and injected:', {
          tagId,
          injectedSection: remediationSection.id,
          currentSection: currentSectionId,
        });
      } catch (err) {
        console.error('[RemediationEngine] Error injecting remediation:', err);
        return {
          triggered: false,
          reason: 'injection_error',
          tagId,
          error: err.message,
        };
      }
    }

    // Record remediation event
    await this.recordRemediationEvent(userId, courseId, tagId, remediationSection.id);

    return {
      triggered: true,
      tagId,
      remediationSectionId: remediationSection.id,
      remediationSectionTitle: remediationSection.title,
      reason: triggerInfo.reason,
      consecutiveErrors: triggerInfo.consecutiveIncorrect,
      mastery: (triggerInfo.masteryPercent * 100).toFixed(1) + '%',
    };
  }

  /**
   * Get remediation content for a tag
   * Returns content from the same section/topic for review
   */
  async getRemediationContent(userId, courseId, tagId) {
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) {
      return null;
    }

    // Find best section for this tag
    const targetSection = await this.findSectionForTag(courseId, tagId);
    if (!targetSection) {
      return null;
    }

    // Find all content items from the target section
    const contentItems = [];

    // Get section description/content
    if (targetSection.description) {
      contentItems.push({
        type: 'section_description',
        sectionId: targetSection.id,
        sectionTitle: targetSection.title,
        content: targetSection.description,
        priority: 1,
      });
    }

    // Get example blocks for this section
    if (targetSection.examples && Array.isArray(targetSection.examples)) {
      targetSection.examples.forEach((example, index) => {
        contentItems.push({
          type: 'example',
          sectionId: targetSection.id,
          sectionTitle: targetSection.title,
          title: example.title,
          content: example.content,
          index,
          priority: 2,
        });
      });
    }

    console.log('[RemediationEngine] Found remediation content items for tag:', {
      tagId,
      section: targetSection.id,
      itemCount: contentItems.length,
    });

    return contentItems.length > 0 ? contentItems : null;
  }

  /**
   * Record remediation event
   */
  async recordRemediationEvent(userId, courseId, tagId, sectionId) {
    try {
      // Store in progress tracking
      const progress = await this.progressRepository.getUserProgress(userId, courseId);
      if (!progress) {
        return null;
      }

      // Add remediation event to tracking
      if (!progress.remediationEvents) {
        progress.remediationEvents = [];
      }

      progress.remediationEvents.push({
        tagId,
        sectionId,
        timestamp: new Date().toISOString(),
      });

      await this.progressRepository.updateUserProgress(progress);

      console.log('[RemediationEngine] Recorded remediation event:', {
        tagId,
        sectionId,
      });
      
      return true;
    } catch (err) {
      console.error('[RemediationEngine] Error recording remediation event:', err);
      return null;
    }
  }

  /**
   * Check if tag needs remediation (has low mastery AND recent errors)
   */
  async needsRemediation(userId, courseId, tagId, sessionId = null) {
    const trigger = await this.checkRemediationTrigger(userId, courseId, tagId, sessionId);
    if (!trigger || !trigger.shouldTrigger) {
      return false;
    }

    return true;
  }

  /**
   * Get all tags currently needing remediation
   */
  async getTagsNeedingRemediation(userId, courseId, sessionId = null) {
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) {
      return [];
    }

    // Collect all unique tags
    const allTags = new Set();
    for (const section of course.sections) {
      const questions = await this.courseRepository.getQuestionsForSection(courseId, section.id);
      for (const question of questions) {
        if (question.tags && Array.isArray(question.tags)) {
          question.tags.forEach(tag => allTags.add(tag));
        }
      }
    }

    // Check each tag
    const needingRemediation = [];
    for (const tag of allTags) {
      const trigger = await this.checkRemediationTrigger(userId, courseId, tag, sessionId);
      
      if (trigger?.shouldTrigger) {
        // Check if not on cooldown
        let onCooldown = false;
        if (sessionId) {
          const session = await repositoryFactory.getSessionRepository().getSessionById(sessionId);
          if (session) {
            onCooldown = adaptiveSessionStateManager.isRemediationOnCooldown(session, tag);
          }
        }

        if (!onCooldown) {
          const section = await this.findSectionForTag(courseId, tag);
          needingRemediation.push({
            tagId: tag,
            mastery: (trigger.masteryPercent * 100).toFixed(1),
            consecutiveErrors: trigger.consecutiveIncorrect,
            remediationSectionId: section?.id,
            remediationSectionTitle: section?.title,
            reason: trigger.reason,
          });
        }
      }
    }

    console.log('[RemediationEngine] Tags needing remediation:', needingRemediation.length);
    return needingRemediation;
  }

  /**
   * Get cooldown status for remediation (legacy compatibility)
   * Prevents showing remediation multiple times in short succession
   */
  getRemediationCooldown(lastRemediationTime, cooldownMinutes = 30) {
    if (!lastRemediationTime) {
      return { onCooldown: false, minutesRemaining: 0 };
    }

    const now = new Date();
    const elapsed = (now - new Date(lastRemediationTime)) / (1000 * 60);
    const minutesRemaining = Math.max(0, cooldownMinutes - elapsed);

    return {
      onCooldown: minutesRemaining > 0,
      minutesRemaining: Math.ceil(minutesRemaining),
    };
  }
}

// Export singleton instance
export const remediationEngine = new RemediationEngine();
